import { apiService } from './api';
import { logger } from '../utils/logger';


export interface User {
  id: string;
  cmsUserId?: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  roles: string[];
  notes?: string;
  dateCreated: string;
  lastLogin?: string;
  isActive: boolean;
}

export interface CreateUserData {
  cmsUserId: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  roles: string[];
  password?: string;
  notes?: string;
}

export interface UpdateUserData {
  cmsUserId?: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  roles: string[];
  password?: string;
  notes?: string;
  isActive?: boolean; // User status: true = Hoạt động, false = Tạm dừng (only Admin/Approver can update)
}

export const userService = {
  /**
   * Get all users
   */
  async getUsers(): Promise<User[]> {
    logger.log('📡 userService.getUsers() - Calling API...');
    try {
      const response = await apiService.get<{ users: User[] }>('/api/users');
      logger.log('📡 userService.getUsers() - API response:', response);
      logger.log('📡 userService.getUsers() - Users count:', response.users?.length || 0);
      logger.log('📡 userService.getUsers() - Users:', response.users?.map(u => ({
        id: u.id,
        cmsUserId: u.cmsUserId,
        fullName: u.fullName,
        email: u.email
      })));
      
      if (!response || !response.users) {
        logger.error('❌ userService.getUsers() - Invalid response:', response);
        throw new Error('Invalid response from API');
      }
      
      if (!Array.isArray(response.users)) {
        logger.error('❌ userService.getUsers() - Users is not an array:', response.users);
        throw new Error('Users is not an array');
      }
      
      logger.log('✅ userService.getUsers() - Returning', response.users.length, 'users');
      return response.users;
    } catch (error) {
      logger.error('❌ userService.getUsers() - Error:', error);
      throw error;
    }
  },

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserData): Promise<User> {
    logger.log('📡 userService.createUser() - Calling API with:', userData);
    const response = await apiService.post<{ success: boolean; user: User }>('/api/users', userData);
    logger.log('📡 userService.createUser() - API response:', response);
    logger.log('📡 userService.createUser() - Created user:', response.user);
    return response.user;
  },

  /**
   * Update an existing user
   */
  async updateUser(userId: string, userData: UpdateUserData): Promise<User> {
    const response = await apiService.put<{ success: boolean; user: User }>(`/api/users/${userId}`, userData);
    return response.user;
  },

  /**
   * Update user status only (by ID). This avoids requiring email/fullName/roles,
   * which is important for Oracle-synced users missing email.
   */
  async updateUserStatus(userId: string, isActive: boolean): Promise<User> {
    const response = await apiService.patch<{ success: boolean; user: User }>(`/api/users/${userId}/status`, { isActive });
    return response.user;
  },

  /**
   * Delete a user
   */
  async deleteUser(userId: string): Promise<void> {
    await apiService.delete<{ success: boolean; message: string }>(`/api/users/${userId}`);
  },

  /**
   * Bulk delete users
   */
  async bulkDeleteUsers(userIds: string[]): Promise<{ success: boolean; message: string; deletedCount: number; errors?: string[] }> {
    const response = await apiService.delete<{ success: boolean; message: string; deletedCount: number; errors?: string[] }>('/api/users/bulk', {
      ids: userIds
    });
    return response;
  },

  async exportUsers(dateFrom?: Date | null, dateTo?: Date | null): Promise<User[]> {
    const searchParams = new URLSearchParams();
    if (dateFrom) {
      searchParams.append('dateFrom', dateFrom.toISOString().split('T')[0]);
    }
    if (dateTo) {
      searchParams.append('dateTo', dateTo.toISOString().split('T')[0]);
    }
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/users/export?${queryString}` : `/api/users/export`;
    
    const response = await apiService.get<{ users: User[] }>(endpoint);
    return response.users || [];
  }
};

